
// =====================================================
// IMPORTS
// =====================================================

import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    Table,
    Card,
    Row,
    Col,
    Statistic,
    Button,
    Space,
    Typography,
    Spin,
    Empty,
    message,
    Tag,
    Avatar,
    Divider,
    Progress,
    Tooltip,
} from "antd";

import {
    DownloadOutlined,
    DollarCircleOutlined,
    WalletOutlined,
    FileTextOutlined,
    ReloadOutlined,
    UserOutlined,
    CalendarOutlined,
    IdcardOutlined,
    BookOutlined,
    ArrowLeftOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
} from "@ant-design/icons";

import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";

import api from "../../api/api";
import { useAcademicYear } from "../../AcademicYearContext";
import StudentPaymentHistoryPDF from "./StudentPaymentHistoryPDF";

const { Title, Text } = Typography;

// =====================================================
// COLORS
// =====================================================

const COLORS = {
    navy: "#062A4B",
    deepNavy: "#031B35",
    blue: "#0B5688",
    gold: "#D9A928",
    goldLight: "#FFF7DF",
    green: "#16834A",
    greenLight: "#EAF7EF",
    red: "#C43D43",
    redLight: "#FFF0F0",
    blueLight: "#EDF5FC",
    background: "#F4F7FA",
    border: "#E5EAF0",
    text: "#172B40",
    muted: "#718096",
    white: "#FFFFFF",
};

// =====================================================
// COMPONENT
// =====================================================

const TransActionPerStudent = ({
    selectedChild,
    onBack,
}) => {
    const { selectedYear } = useAcademicYear();

    // =================================================
    // STATES
    // =================================================

    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [student, setStudent] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [academicYear, setAcademicYear] = useState(null);

    // =================================================
    // HELPERS
    // =================================================

    const money = useCallback((value) => {
        return Number(value || 0).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    }, []);

    const formatDate = useCallback((date) => {
        if (!date) return "-";

        const parsedDate = new Date(date);

        if (Number.isNaN(parsedDate.getTime())) {
            return "-";
        }

        return parsedDate.toLocaleDateString(undefined, {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    }, []);

    const getImageUrl = useCallback((image) => {
        if (!image) return undefined;

        if (
            image.startsWith("http://") ||
            image.startsWith("https://")
        ) {
            return image;
        }

        return image.startsWith("/")
            ? image
            : `/${image}`;
    }, []);

    // =================================================
    // FETCH TRANSACTIONS
    // =================================================

    const fetchTransactions = useCallback(async () => {
        if (!selectedYear?.id || !selectedChild?.id) {
            return;
        }

        setLoading(true);

        try {
            const response = await api.get(
                "/student-payment-history/",
                {
                    params: {
                        academic_year: selectedYear.id,
                        student: selectedChild.id,
                    },
                }
            );

            const data = response.data;

            setStudent({
                id: selectedChild.id,
                full_name: selectedChild.full_name,
                grade_class_name:
                    selectedChild.grade_class_name,
                student_image:
                    selectedChild.student_admission_image,
            });

            setTransactions(
                Array.isArray(data?.results)
                    ? data.results
                    : []
            );

            setAcademicYear(selectedYear);

        } catch (error) {
            console.error(
                "Payment history loading error:",
                error
            );

            message.error(
                error?.response?.data?.detail ||
                error?.response?.data?.message ||
                "Unable to load payment transactions."
            );

        } finally {
            setLoading(false);
        }
    }, [selectedYear, selectedChild]);

    // =================================================
    // INITIAL LOAD
    // =================================================

    useEffect(() => {
        fetchTransactions();
    }, [fetchTransactions]);

    // =================================================
    // FINANCIAL TOTALS
    // =================================================

    const totalPaid = useMemo(() => {
        return transactions.reduce(
            (sum, item) =>
                sum + Number(item.amount_paid || 0),
            0
        );
    }, [transactions]);

    const outstandingBalance = useMemo(() => {
        return transactions.reduce(
            (sum, item) =>
                sum + Number(item.balance || 0),
            0
        );
    }, [transactions]);

    const totalTransactions = transactions.length;

    const paidTransactions = useMemo(() => {
        return transactions.filter(
            (item) => Number(item.amount_paid || 0) > 0
        ).length;
    }, [transactions]);

    const paymentProgress = useMemo(() => {
        const total = totalPaid + outstandingBalance;

        if (total <= 0) return 0;

        return Math.min(
            100,
            Math.round((totalPaid / total) * 100)
        );
    }, [totalPaid, outstandingBalance]);

    // =================================================
    // PDF DOWNLOAD
    // =================================================

    const downloadPDF = async () => {
        if (!student) {
            message.warning(
                "Student information not found."
            );
            return;
        }

        setDownloading(true);

        try {
            const document = (
                <StudentPaymentHistoryPDF
                    student={student}
                    transactions={transactions}
                    academicYear={academicYear}
                />
            );

            const blob = await pdf(document).toBlob();

            saveAs(
                blob,
                `${student.full_name?.replace(/\s+/g, "_")}_Payment_Statement.pdf`
            );

            message.success(
                "Payment statement downloaded successfully."
            );

        } catch (error) {
            console.error(error);

            message.error(
                "Failed to generate payment statement."
            );

        } finally {
            setDownloading(false);
        }
    };

    // =================================================
    // TABLE COLUMNS
    // =================================================

    const columns = useMemo(() => [
        {
            title: "NO.",
            key: "number",
            width: 75,
            align: "center",
            render: (_, __, index) => (
                <Text
                    strong
                    style={{ color: COLORS.muted }}
                >
                    {index + 1}
                </Text>
            ),
        },

        {
            title: "PAYMENT DATE",
            dataIndex: "created_at",
            key: "created_at",
            width: 145,
            render: (value) => (
                <Space size={7}>
                    <CalendarOutlined
                        style={{ color: COLORS.muted }}
                    />
                    <Text>
                        {formatDate(value)}
                    </Text>
                </Space>
            ),
        },

        {
            title: "CATEGORY",
            dataIndex: "payment_category",
            key: "payment_category",
            width: 155,
            render: (value) => (
                <Tag
                    style={{
                        borderRadius: 5,
                        padding: "3px 9px",
                        margin: 0,
                        background: COLORS.blueLight,
                        color: COLORS.blue,
                        border: "none",
                        fontWeight: 600,
                    }}
                >
                    {value || "General"}
                </Tag>
            ),
        },

        {
            title: "DESCRIPTION",
            dataIndex: "fee_type",
            key: "fee_type",
            render: (value) => (
                <Text
                    strong
                    style={{ color: COLORS.text }}
                >
                    {value || "-"}
                </Text>
            ),
        },

        {
            title: "AMOUNT PAID",
            dataIndex: "amount_paid",
            key: "amount_paid",
            align: "right",
            width: 165,
            render: (value) => (
                <Text
                    strong
                    style={{
                        color: COLORS.green,
                        fontVariantNumeric: "tabular-nums",
                    }}
                >
                    ${money(value)}
                </Text>
            ),
        },

        {
            title: "BALANCE",
            dataIndex: "balance",
            key: "balance",
            align: "right",
            width: 155,
            render: (value) => {
                const amount = Number(value || 0);

                return (
                    <Text
                        strong
                        style={{
                            color:
                                amount > 0
                                    ? COLORS.red
                                    : COLORS.green,
                            fontVariantNumeric: "tabular-nums",
                        }}
                    >
                        ${money(amount)}
                    </Text>
                );
            },
        },

        {
            title: "PROCESSED BY",
            dataIndex: "created_by",
            key: "created_by",
            width: 175,
            render: (value) => (
                <Space size={7}>
                    <UserOutlined
                        style={{ color: COLORS.muted }}
                    />
                    <Text type="secondary">
                        {value || "-"}
                    </Text>
                </Space>
            ),
        },
    ], [formatDate, money]);

    // =================================================
    // STUDENT INFORMATION HEADER
    // =================================================

    const StudentSummary = () => (
        <Card
            bordered={false}
            style={{
                borderRadius: 15,
                marginBottom: 22,
                boxShadow: "0 3px 15px rgba(15, 40, 65, 0.055)",
                overflow: "hidden",
            }}
            styles={{
                body: { padding: 0 },
            }}
        >
            <div
                style={{
                    height: 5,
                    background: `linear-gradient(90deg, ${COLORS.navy}, ${COLORS.gold})`,
                }}
            />

            <div style={{ padding: "23px 25px" }}>
                <Row
                    gutter={[20, 20]}
                    align="middle"
                >
                    <Col xs={24} lg={15}>
                        <Space
                            size={17}
                            align="center"
                        >
                            <Avatar
                                size={76}
                                src={getImageUrl(
                                    student?.student_image
                                )}
                                icon={<UserOutlined />}
                                style={{
                                    background: COLORS.navy,
                                    border: `3px solid ${COLORS.gold}`,
                                    flexShrink: 0,
                                }}
                            />

                            <div style={{ minWidth: 0 }}>
                                <Text
                                    type="secondary"
                                    style={{
                                        fontSize: 11,
                                        fontWeight: 700,
                                        letterSpacing: 1.2,
                                    }}
                                >
                                    STUDENT PAYMENT ACCOUNT
                                </Text>

                                <Title
                                    level={4}
                                    style={{
                                        margin: "5px 0 10px",
                                        color: COLORS.navy,
                                    }}
                                >
                                    {student?.full_name || "Student"}
                                </Title>

                                <Space
                                    wrap
                                    size={[8, 8]}
                                >
                                    <Tag
                                        icon={<IdcardOutlined />}
                                        style={{
                                            margin: 0,
                                            borderRadius: 5,
                                            padding: "4px 9px",
                                            color: COLORS.navy,
                                            background: COLORS.blueLight,
                                            border: "none",
                                        }}
                                    >
                                        Admission No: {student?.id || "-"}
                                    </Tag>

                                    <Tag
                                        icon={<BookOutlined />}
                                        style={{
                                            margin: 0,
                                            borderRadius: 5,
                                            padding: "4px 9px",
                                            color: "#946A0B",
                                            background: COLORS.goldLight,
                                            border: "none",
                                        }}
                                    >
                                        {student?.grade_class_name || "Grade not specified"}
                                    </Tag>
                                </Space>
                            </div>
                        </Space>
                    </Col>

                    <Col
                        xs={24}
                        lg={9}
                        style={{ textAlign: "right" }}
                    >
                        <div
                            style={{
                                display: "inline-flex",
                                flexDirection: "column",
                                alignItems: "flex-end",
                                gap: 12,
                            }}
                        >
                            <Tag
                                icon={<CalendarOutlined />}
                                style={{
                                    padding: "5px 11px",
                                    borderRadius: 6,
                                    background: "#F2F5F8",
                                    color: COLORS.text,
                                    border: "none",
                                    fontSize: 12,
                                }}
                            >
                                Academic Year: {academicYear?.name || "-"}
                            </Tag>

                            <Space wrap>
                                {onBack && (
                                    <Button
                                        icon={<ArrowLeftOutlined />}
                                        onClick={onBack}
                                    >
                                        Back
                                    </Button>
                                )}

                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={fetchTransactions}
                                    loading={loading}
                                >
                                    Refresh
                                </Button>

                                <Button
                                    type="primary"
                                    icon={<DownloadOutlined />}
                                    loading={downloading}
                                    onClick={downloadPDF}
                                    style={{
                                        background: COLORS.navy,
                                        borderColor: COLORS.navy,
                                        borderRadius: 7,
                                        fontWeight: 600,
                                    }}
                                >
                                    Download Statement
                                </Button>
                            </Space>
                        </div>
                    </Col>
                </Row>
            </div>
        </Card>
    );

    // =================================================
    // FINANCIAL SUMMARY CARD
    // =================================================

    const FinancialCard = ({
        title,
        value,
        icon,
        color,
        background,
        description,
        prefix = "$",
        isCurrency = true,
    }) => (
        <Card
            bordered={false}
            style={{
                height: "100%",
                borderRadius: 13,
                boxShadow: "0 3px 13px rgba(15, 40, 65, 0.045)",
            }}
            styles={{
                body: { padding: "21px 20px" },
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    marginBottom: 18,
                }}
            >
                <Text
                    style={{
                        color: COLORS.muted,
                        fontSize: 13,
                        fontWeight: 600,
                    }}
                >
                    {title}
                </Text>

                <div
                    style={{
                        width: 43,
                        height: 43,
                        borderRadius: 11,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background,
                        color,
                        fontSize: 20,
                    }}
                >
                    {icon}
                </div>
            </div>

            <Statistic
                value={value}
                precision={isCurrency ? 2 : 0}
                prefix={isCurrency ? prefix : undefined}
                groupSeparator=","
                valueStyle={{
                    fontSize: 25,
                    fontWeight: 750,
                    color,
                    lineHeight: 1.3,
                }}
            />

            <Divider
                style={{
                    margin: "15px 0 11px",
                    borderColor: COLORS.border,
                }}
            />

            <Text
                type="secondary"
                style={{ fontSize: 12 }}
            >
                {description}
            </Text>
        </Card>
    );

    // =================================================
    // FINANCIAL OVERVIEW
    // =================================================

    const StatisticsCards = () => (
        <>
            <div style={{ marginBottom: 15 }}>
                <Title
                    level={5}
                    style={{
                        color: COLORS.navy,
                        margin: 0,
                    }}
                >
                    Financial Overview
                </Title>

                <Text
                    type="secondary"
                    style={{ fontSize: 12 }}
                >
                    A summary of payments recorded for this academic year.
                </Text>
            </div>

            <Row gutter={[17, 17]}>
                <Col xs={24} sm={12} xl={8}>
                    <FinancialCard
                        title="Total Amount Paid"
                        value={totalPaid}
                        icon={<DollarCircleOutlined />}
                        color={COLORS.green}
                        background={COLORS.greenLight}
                        description={`${paidTransactions} payment record(s) with an amount paid`}
                    />
                </Col>

                <Col xs={24} sm={12} xl={8}>
                    <FinancialCard
                        title="Outstanding Balance"
                        value={outstandingBalance}
                        icon={<WalletOutlined />}
                        color={COLORS.red}
                        background={COLORS.redLight}
                        description={
                            outstandingBalance > 0
                                ? "Amount remaining according to recorded transactions"
                                : "No outstanding balance recorded"
                        }
                    />
                </Col>

                <Col xs={24} sm={24} xl={8}>
                    <FinancialCard
                        title="Total Transactions"
                        value={totalTransactions}
                        isCurrency={false}
                        icon={<FileTextOutlined />}
                        color={COLORS.blue}
                        background={COLORS.blueLight}
                        description="Number of payment records retrieved"
                    />
                </Col>
            </Row>
        </>
    );

    // =================================================
    // PAYMENT PROGRESS
    // =================================================

    const PaymentProgressCard = () => (
        <Card
            bordered={false}
            style={{
                marginTop: 22,
                borderRadius: 13,
                boxShadow: "0 3px 13px rgba(15, 40, 65, 0.045)",
            }}
            styles={{
                body: { padding: "20px 23px" },
            }}
        >
            <Row
                align="middle"
                gutter={[20, 16]}
            >
                <Col xs={24} md={14}>
                    <Space
                        align="center"
                        size={10}
                    >
                        <div
                            style={{
                                width: 38,
                                height: 38,
                                borderRadius: 9,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: COLORS.goldLight,
                                color: "#946A0B",
                                fontSize: 18,
                            }}
                        >
                            <CheckCircleOutlined />
                        </div>

                        <div>
                            <Text
                                strong
                                style={{
                                    color: COLORS.navy,
                                    fontSize: 14,
                                }}
                            >
                                Payment Summary
                            </Text>

                            <div>
                                <Text
                                    type="secondary"
                                    style={{ fontSize: 12 }}
                                >
                                    Based on the retrieved transaction amounts
                                </Text>
                            </div>
                        </div>
                    </Space>
                </Col>

                <Col xs={24} md={10}>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: 8,
                        }}
                    >
                        <Text
                            type="secondary"
                            style={{ fontSize: 12 }}
                        >
                            Paid amount ratio
                        </Text>

                        <Text
                            strong
                            style={{ color: COLORS.green }}
                        >
                            {paymentProgress}%
                        </Text>
                    </div>

                    <Progress
                        percent={paymentProgress}
                        showInfo={false}
                        strokeColor={COLORS.green}
                        trailColor="#E9EEF2"
                        strokeWidth={10}
                    />
                </Col>
            </Row>

            <Divider
                style={{
                    margin: "17px 0",
                    borderColor: COLORS.border,
                }}
            />

            <Row gutter={[16, 12]}>
                <Col xs={24} sm={12}>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 8,
                        }}
                    >
                        <Text type="secondary">
                            Total Paid
                        </Text>

                        <Text
                            strong
                            style={{ color: COLORS.green }}
                        >
                            ${money(totalPaid)}
                        </Text>
                    </div>
                </Col>

                <Col xs={24} sm={12}>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 8,
                        }}
                    >
                        <Text type="secondary">
                            Outstanding Balance
                        </Text>

                        <Text
                            strong
                            style={{ color: COLORS.red }}
                        >
                            ${money(outstandingBalance)}
                        </Text>
                    </div>
                </Col>
            </Row>
        </Card>
    );

    // =================================================
    // TRANSACTIONS TABLE
    // =================================================

    const TransactionsTable = () => (
        <Card
            bordered={false}
            style={{
                marginTop: 23,
                borderRadius: 14,
                boxShadow: "0 3px 15px rgba(15, 40, 65, 0.05)",
            }}
            styles={{
                body: { padding: 0 },
            }}
        >
            <div
                style={{
                    padding: "20px 22px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    flexWrap: "wrap",
                }}
            >
                <div>
                    <Title
                        level={5}
                        style={{
                            margin: 0,
                            color: COLORS.navy,
                        }}
                    >
                        Payment Transaction History
                    </Title>

                    <Text
                        type="secondary"
                        style={{ fontSize: 12 }}
                    >
                        Review individual payments and outstanding balances.
                    </Text>
                </div>

                <Tag
                    style={{
                        margin: 0,
                        border: "none",
                        borderRadius: 6,
                        padding: "5px 11px",
                        background: COLORS.blueLight,
                        color: COLORS.blue,
                        fontWeight: 600,
                    }}
                >
                    {totalTransactions} Transaction(s)
                </Tag>
            </div>

            <Divider
                style={{
                    margin: 0,
                    borderColor: COLORS.border,
                }}
            />

            <div style={{ padding: "5px 18px 18px" }}>
                <Table
                    rowKey={(record, index) =>
                        record.id || index
                    }
                    columns={columns}
                    dataSource={transactions}
                    loading={loading}
                    bordered={false}
                    size="middle"
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        pageSizeOptions: [10, 20, 50, 100],
                        showTotal: (total, range) =>
                            `Showing ${range[0]}–${range[1]} of ${total} transactions`,
                    }}
                    scroll={{
                        x: 1100,
                    }}
                    rowClassName={() => "payment-history-row"}
                    locale={{
                        emptyText: (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    <div style={{ padding: "12px 0" }}>
                                        <Text
                                            strong
                                            style={{
                                                display: "block",
                                                color: COLORS.text,
                                            }}
                                        >
                                            No payment transactions found
                                        </Text>

                                        <Text
                                            type="secondary"
                                            style={{ fontSize: 12 }}
                                        >
                                            Payment records for this student and academic year will appear here.
                                        </Text>
                                    </div>
                                }
                            />
                        ),
                    }}
                />
            </div>
        </Card>
    );

    // =================================================
    // RENDER
    // =================================================

    return (
        <div
            style={{
                minHeight: "100%",
                padding: "24px",
                background: COLORS.background,
            }}
        >
            {/* PAGE HEADER */}

            <div
                style={{
                    marginBottom: 23,
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 15,
                    flexWrap: "wrap",
                }}
            >
                <div>
                    <Text
                        style={{
                            color: COLORS.gold,
                            fontWeight: 700,
                            fontSize: 11,
                            letterSpacing: 1.5,
                        }}
                    >
                        STUDENT FINANCIAL RECORDS
                    </Text>

                    <Title
                        level={2}
                        style={{
                            margin: "5px 0 5px",
                            color: COLORS.navy,
                            fontWeight: 750,
                        }}
                    >
                        Payment Transactions
                    </Title>

                    <Text
                        style={{
                            color: COLORS.muted,
                            fontSize: 13,
                        }}
                    >
                        View payment history, monitor outstanding balances,
                        and download the student's payment statement.
                    </Text>
                </div>

                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 7,
                        padding: "8px 12px",
                        background: COLORS.white,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: 8,
                    }}
                >
                    <ClockCircleOutlined
                        style={{ color: COLORS.blue }}
                    />

                    <Text
                        type="secondary"
                        style={{ fontSize: 12 }}
                    >
                        {academicYear?.name || selectedYear?.name || "Academic Year"}
                    </Text>
                </div>
            </div>

            {/* MAIN CONTENT */}

            {loading && !student ? (
                <Card
                    bordered={false}
                    style={{ borderRadius: 14 }}
                >
                    <div
                        style={{
                            minHeight: 330,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 15,
                        }}
                    >
                        <Spin size="large" />

                        <Text
                            type="secondary"
                            style={{ fontSize: 13 }}
                        >
                            Loading student payment records...
                        </Text>
                    </div>
                </Card>
            ) : !student ? (
                <Card
                    bordered={false}
                    style={{ borderRadius: 14 }}
                >
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="No student payment information found."
                    />

                    <div style={{ textAlign: "center" }}>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={fetchTransactions}
                            loading={loading}
                        >
                            Try Again
                        </Button>
                    </div>
                </Card>
            ) : (
                <>
                    <StudentSummary />

                    <StatisticsCards />

                    <PaymentProgressCard />

                    <TransactionsTable />
                </>
            )}

            {/* SMALL UI POLISH */}

            <style>
                {`
                    .payment-history-row td {
                        padding-top: 15px !important;
                        padding-bottom: 15px !important;
                    }

                    .payment-history-row:hover td {
                        background: #F5F9FD !important;
                    }

                    .ant-table-wrapper .ant-table-thead > tr > th {
                        background: #F5F8FB !important;
                        color: #536477 !important;
                        font-size: 11px !important;
                        font-weight: 700 !important;
                        letter-spacing: 0.45px;
                        padding-top: 14px !important;
                        padding-bottom: 14px !important;
                        white-space: nowrap;
                    }

                    .ant-table-wrapper .ant-table-cell {
                        border-bottom-color: #E9EEF3 !important;
                    }

                    @media (max-width: 576px) {
                        .payment-history-row td {
                            padding-top: 12px !important;
                            padding-bottom: 12px !important;
                        }
                    }
                `}
            </style>
        </div>
    );
};

// =====================================================
// EXPORT
// =====================================================

export default TransActionPerStudent;
